// @flow
/* eslint arrow-parens:0 */
import type { Action } from 'app/common/redux/types';
import { UserActionTypes } from 'app/common/redux/actions/user/actionTypes';
import boardInfoReducer, { type State as BoardInfo } from 'app/common/redux/reducers/board';

export const BOARD_BASE_RESOURCES = [
  'BoardsResource',
  'BoardFollowingResource',
  'SearchResource',
  'BoardArchiveResource',
  'BoardIdeasCardFeedResource',
  'MoreIdeasTabsResource',
  'BoardSoftDeleteResource',
];

export const RECOMMENDATION_REASON = {
  RECENTLY_PICKED: 'Recently picked',
};

export const MAX_NUM_RECOMMENDATIONS = 3;

export type FetchingState = { [id: string]: true };
const fetching = (state: FetchingState, action: Action): FetchingState => {
  if (action.type === 'RESOURCE_FETCH_CREATE') {
    const { payload } = action;
    if (
      payload.name === 'BoardResource' ||
      payload.name === 'BoardPageResource' ||
      payload.name === 'BoardArchiveResource' ||
      payload.name === 'BoardSoftDeleteResource'
    ) {
      const id = payload.options.board_id;
      return {
        ...state,
        [id]: true,
      };
    }
  } else if (action.type === 'RESOURCE_FETCH_COMPLETE') {
    const { payload } = action;
    if (
      payload.name === 'BoardResource' ||
      payload.name === 'BoardPageResource' ||
      payload.name === 'BoardArchiveResource' ||
      payload.name === 'BoardSoftDeleteResource'
    ) {
      const id = payload.options.board_id;
      const nextState = { ...state };
      if (nextState[id]) {
        delete nextState[id];
      }
      return nextState;
    }
  }
  return state;
};

export type DeletingState = {
  [id: string]: {
    deleting: boolean,
    error: Object,
    success: boolean,
  },
};

const deleting = (state: DeletingState, action: Action): DeletingState => {
  if (action.type === 'BOARD_DELETE') {
    const nextState = { ...state };
    nextState[action.payload.boardId] = { deleting: true };
    return nextState;
  } else if (action.type === 'BOARD_DELETE_COMPLETE') {
    const {
      error,
      payload: { boardId },
    } = action;
    if (error) {
      const nextState = { ...state };
      nextState[boardId] = {
        deleting: false,
        error,
        success: false,
      };
      return nextState;
    } else {
      const nextState = { ...state };
      nextState[boardId] = {
        deleting: false,
        success: true,
      };
      return nextState;
    }
  }
  return state;
};

export type State = {
  content: {
    [id: string]: BoardInfo,
  },
  deleting: DeletingState,
  fetchAllComplete: boolean,
  fetching: FetchingState,
  lastPinnedTo: ?Array<{
    id: string,
    name: string,
  }>,
};

const initialState = {
  content: {},
  deleting: {},
  fetching: {},
  fetchAllComplete: false,
  // TODO: normalize field
  lastPinnedTo: null,
};

const boardsReducer = (state: State = initialState, action: Action): State => {
  if (action.type === 'RESOURCE_FETCH_COMPLETE' || action.type === 'RESOURCE_FETCH_MORE_COMPLETE') {
    const { payload } = action;
    if (payload.name === 'BoardPickerBoardsShortlistResource') {
      const boards = payload.data || [];
      const nextState = {
        ...state,
        fetching: fetching(state.fetching, action),
      };
      if (!nextState.lastPinnedTo) {
        nextState.lastPinnedTo = boards;
      }
      return nextState;
    } else if (BOARD_BASE_RESOURCES.includes(payload.name) && payload.data) {
      const boards = payload.data || [];
      const nextState = {
        ...state,
        fetching: fetching(state.fetching, action),
        fetchAllComplete:
          payload.name === 'BoardsResource' ||
          payload.name === 'BoardArchiveResource' ||
          payload.name === 'BoardSoftDeleteResource',
      };
      boards.forEach((board) => {
        if (!board.type || board.type === 'board') {
          nextState.content[board.id] = {
            ...nextState.content[board.id],
            ...board,
          };
        }
      });
      return nextState;
    } else if (
      payload.name === 'BoardResource' ||
      payload.name === 'BoardPageResource' ||
      payload.name === 'BoardActionStatusResource'
    ) {
      // For the board page, we pass in a slug instead of an id when fetching
      const id = payload.options.board_id || (payload.data || {}).id;
      const { content = {} } = state;
      return {
        ...state,
        content: {
          ...content,
          [id]: boardInfoReducer(content[id], action),
        },
        fetching: fetching(state.fetching, action),
      };
    }
  } else if (action.type === 'BOARD_CREATE_COMPLETE') {
    const { payload } = action;
    const { board } = payload;
    if (board && board.id) {
      return {
        ...state,
        content: {
          ...state.content,
          [board.id]: {
            ...state.content[board.id],
            ...board,
          },
        },
      };
    }
    return state;
  } else if (action.type === 'BOARD_UPDATE_COMPLETE') {
    const { payload } = action;
    const { board } = payload;
    return {
      ...state,
      content: {
        ...state.content,
        [board.id]: {
          ...state.content[board.id],
          ...board,
          sensitivity: board.sensitivity || null,
        },
      },
    };
  } else if (action.type === 'BOARD_DELETE') {
    return {
      ...state,
      deleting: deleting(state.deleting, action),
    };
  } else if (action.type === 'BOARD_DELETE_COMPLETE') {
    const { boardId } = action.payload;
    const nextState = {
      ...state,
      deleting: deleting(state.deleting, action),
    };
    delete nextState.content[boardId];
    return nextState;
  } else if (
    action.type === 'BOARD_COVER_IMAGE_UPDATED' ||
    action.type === 'BOARD_DISABLE_MORE_IDEAS_BADGING' ||
    action.type === 'BOARD_FOLLOW' ||
    action.type === 'BOARD_INVITE_ACCEPTED_BY_VIEWER' ||
    action.type === 'BOARD_PIN_COUNT_CHANGED' ||
    action.type === 'BOARD_ARCHIVED' ||
    action.type === 'BOARD_UNARCHIVED' ||
    action.type === 'BOARD_REQUEST_TO_JOIN' ||
    action.type === 'BOARD_UPDATE_COLLABORATION_SETTINGS' ||
    action.type === 'SECTION_ADDED' ||
    action.type === 'SECTION_DELETED' ||
    action.type === 'BOARD_RESTORED'
  ) {
    const {
      payload: { boardId },
    } = action;
    const { content } = state;
    return {
      ...state,
      content: {
        ...content,
        [boardId]: boardInfoReducer(content[boardId], action),
      },
    };
  } else if (action.type === UserActionTypes.USER_FOLLOW) {
    const { content } = state;
    const { id: userId } = action.payload;
    const userBoardIds = Object.keys(content).filter(
      (boardId) => content[boardId].owner && content[boardId].owner.id === userId,
    );
    const updatedBoards = userBoardIds.reduce((currentMap, boardId) => {
      currentMap[boardId] = boardInfoReducer(content[boardId], action);
      return currentMap;
    }, {});
    return {
      ...state,
      content: {
        ...content,
        ...updatedBoards,
      },
    };
  } else if (action.type === 'BOARD_PIN_TO') {
    const { payload } = action;
    const { board: lastBoard } = payload;
    const nextState = { ...state };
    if (nextState.lastPinnedTo) {
      const lastPinnedTo = nextState.lastPinnedTo.filter((board) => board.id !== lastBoard.id);
      nextState.lastPinnedTo = [lastBoard].concat(lastPinnedTo);
    } else {
      nextState.lastPinnedTo = [lastBoard];
    }

    return nextState;
  }

  return {
    ...state,
    fetching: fetching(state.fetching, action),
  };
};

export default boardsReducer;
